import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import OrderFormModal from './components/OrderFormModal';
import SeoHead from '../../components/feature/SeoHead';

interface Review {
  id?: string;
  name: string;
  rating: number;
  review: string;
  date: string;
  avatar?: string;
}

export default function Home() {
  const [allReviews, setAllReviews] = useState<Review[]>([]);
  const [orderModal, setOrderModal] = useState<{ open: boolean; productName: string; formAction: string }>({
    open: false,
    productName: '',
    formAction: '',
  });

  const products = [
    {
      name: 'Honey Lemonade Coffee',
      image: 'https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/314c276d35d4136a927070de3ee71152.png',
      description: 'Rich, smooth cold brew coffee served over ice with a hint of sweetness',
      formAction: 'https://readdy.ai/api/form/d6dti9qtehdqnvnpj420',
    },
    {
      name: 'Salted Caramel Latte',
      image: 'https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/96c57818cf59b2f258bd6b3d373ee2a8.png',
      description: 'Smooth, creamy latte infused with rich buttery caramel and a touch of sea salt for the perfect sweet-and-savory balance',
      formAction: 'https://readdy.ai/api/form/d6dtibqtehdqnvnpj430',
    },
    {
      name: 'Coffee Lemonade',
      image: 'https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/ced42289e43aa65757d74fba079f205e.jpeg',
      description: '🍋 When life gives you lemons... we add coffee magic! Stay tuned for more zesty surprises!',
      formAction: 'https://readdy.ai/api/form/d6dtibqtehdqnvnpj43g',
    },
  ];

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const homeJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Lemonade Coffee Store',
      url: siteUrl,
      description: 'Refreshing lemonades and bold Malfi Coffee blends crafted in Orange County, CA.',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/?s={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FoodEstablishment',
      name: 'Lemonade Coffee Store',
      alternateName: 'Malfi Coffee',
      url: siteUrl,
      logo: `${siteUrl}/vite.svg`,
      image: 'https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/dcdc3ad3223627cc9233b520af264f75.png',
      description: 'Orange County family-owned lemonade and coffee store serving signature Honey Lemonade Coffee, Salted Caramel Latte, and Coffee Lemonade fusion drinks. Event catering available.',
      address: {
        '@type': 'PostalAddress',
        addressRegion: 'CA',
        addressLocality: 'Orange County',
        addressCountry: 'US',
      },
      areaServed: {
        '@type': 'State',
        name: 'California',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'info@lemonadecoffeestore.com',
        contactType: 'customer service',
      },
      sameAs: ['https://www.instagram.com/lemonadecoffeestore/'],
      servesCuisine: ['Coffee', 'Lemonade', 'Beverages'],
      hasMenu: `${siteUrl}/gallery`,
      menu: `${siteUrl}/gallery`,
      priceRange: '$$',
      foundingDate: '2025',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Signature Drinks',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Product',
            name: 'Honey Lemonade Coffee',
            description: 'Rich, smooth cold brew coffee served over ice with a hint of sweetness',
            image: 'https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/314c276d35d4136a927070de3ee71152.png',
            brand: { '@type': 'Brand', name: 'Lemonade Coffee Store' },
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Product',
            name: 'Salted Caramel Latte',
            description: 'Smooth, creamy latte infused with rich buttery caramel and a touch of sea salt',
            image: 'https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/96c57818cf59b2f258bd6b3d373ee2a8.png',
            brand: { '@type': 'Brand', name: 'Lemonade Coffee Store' },
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Product',
            name: 'Coffee Lemonade',
            description: 'A bold espresso and fresh lemon fusion drink',
            image: 'https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/ced42289e43aa65757d74fba079f205e.jpeg',
            brand: { '@type': 'Brand', name: 'Lemonade Coffee Store' },
          },
        },
      ],
    },
  ];

  useEffect(() => {
    // Hardcoded reviews
    const staticReviews: Review[] = [
      {
        name: 'Sarah Mitchell',
        rating: 5,
        review: 'Absolutely love the Coffee Lemonade! I was skeptical at first, but the combination of bold espresso and fresh lemon is incredible. This is now my go-to summer drink. The staff is always so friendly and welcoming!',
        date: 'May 2025',
        avatar: 'https://public.readdy.ai/ai/img_res/aa2d751ab8c148f3d9c7e3ecd0ad9971.jpg'
      },
      {
        name: 'James Rodriguez',
        rating: 5,
        review: 'Best Lemonade Coffee, hands down. The quality of the beans really shines through. The owners are nice and friendly. I would recommend ordering this drink if you like iced tea.',
        date: 'April 2025',
        avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20smiling%20young%20man%20with%20dark%20hair%20and%20light%20stubble%20in%20warm%20natural%20lighting%2C%20friendly%20expression%2C%20casual%20professional%20look%2C%20headshot%20photography%20with%20soft%20background&width=100&height=100&seq=review-avatar-james-2&orientation=squarish'
      },
      {
        name: 'Emily Chen',
        rating: 5,
        review: 'The Classic Lemonade here is perfection — not too sweet, not too tart, just right. I also tried the signature blend and was blown away. This drink has such a charming vibe. Highly recommend to anyone looking for something unique!',
        date: 'March 2025',
        avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20smiling%20young%20Asian%20woman%20with%20long%20black%20hair%20in%20soft%20warm%20lighting%2C%20cheerful%20expression%2C%20casual%20elegant%20style%2C%20headshot%20photography%20with%20blurred%20background&width=100&height=100&seq=review-avatar-emily-3&orientation=squarish'
      },
      {
        name: 'David Thompson',
        rating: 4,
        review: 'Great spot for a midday pick-me-up. The coffee is rich and flavorful, and the lemonade options are super refreshing.',
        date: 'March 2025',
        avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20smiling%20middle%20aged%20man%20with%20short%20gray%20hair%20and%20glasses%20in%20warm%20lighting%2C%20kind%20expression%2C%20smart%20casual%20style%2C%20headshot%20photography%20with%20neutral%20background&width=100&height=100&seq=review-avatar-david-4&orientation=squarish'
      },
      {
        name: 'Olivia Parker',
        rating: 5,
        review: 'I stumbled upon this gem while at an event and loved the taste. Pure magic in a cup!',
        date: 'February 2025',
        avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20smiling%20young%20woman%20with%20blonde%20curly%20hair%20in%20golden%20hour%20lighting%2C%20joyful%20expression%2C%20bohemian%20casual%20style%2C%20headshot%20photography%20with%20warm%20bokeh%20background&width=100&height=100&seq=review-avatar-olivia-5&orientation=squarish'
      },
      {
        name: 'Marcus Lee',
        rating: 5,
        review: 'As a coffee snob, I\'m very picky about where I get my brew. The Lemonade Coffee Store/ Malfi Coffee drink exceeded my expectations. Their beans are top-notch, and the lemon twist adds a whole new dimension. This is a must-visit for any coffee lover.',
        date: 'January 2025',
        avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20smiling%20young%20man%20with%20black%20hair%20and%20clean%20shaven%20face%20in%20natural%20daylight%2C%20confident%20friendly%20expression%2C%20modern%20casual%20style%2C%20headshot%20photography%20with%20minimal%20background&width=100&height=100&seq=review-avatar-marcus-6&orientation=squarish'
      }
    ];

    // Load approved reviews from localStorage
    const approvedReviewsData = localStorage.getItem('approvedReviews');
    const approvedReviews: Review[] = approvedReviewsData ? JSON.parse(approvedReviewsData) : [];

    // Combine and sort by date (newest first)
    const combined = [...approvedReviews, ...staticReviews];
    setAllReviews(combined);
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF8E7]">
      <SeoHead
        title="Lemonade Coffee Store – Fresh Lemonade &amp; Bold Malfi Coffee | Orange County, CA"
        description="Lemonade Coffee Store serves refreshing lemonades and bold Malfi Coffee blends in Orange County, CA. Order our signature Honey Lemonade Coffee, Salted Caramel Latte, and Coffee Lemonade fusion drinks. Catering available for events."
        keywords="lemonade coffee, Malfi Coffee, Orange County coffee shop, honey lemonade coffee, coffee catering"
        canonical={siteUrl}
        ogImage="https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/dcdc3ad3223627cc9233b520af264f75.png"
        jsonLd={homeJsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-0">
        {/* Striped Background */}
        <div className="absolute inset-0 flex">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`flex-1 ${i % 2 === 0 ? 'bg-[#FFD700]' : 'bg-white'}`}
            />
          ))}
        </div>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-6xl mx-auto w-full">
          <div className="mb-8">
            <img 
              src="https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/dcdc3ad3223627cc9233b520af264f75.png"
              alt="Lemonade Coffee Store"
              className="w-full max-w-2xl mx-auto h-auto"
            />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-[#2C5F2D] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Refreshing Lemonade<br />and Bold Coffee
          </h1>
          
          <p className="text-xl md:text-2xl text-[#4A4A4A] mb-8 max-w-2xl mx-auto leading-relaxed">
            Enjoy the refreshing taste of freshly poured lemonade, perfectly paired with our smooth, rich, and bold Malfi Coffee brewed to excite your senses.
          </p>
          
          <Link 
            to="/gallery"
            className="inline-block bg-[#FFD700] text-[#2C5F2D] px-10 py-4 rounded-full text-lg font-bold hover:bg-[#FFC700] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            VIEW OUR MENU
          </Link>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-20 px-4 bg-[#FFF8E7]">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C5F2D] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Welcome to Lemonade Coffee Store
          </h2>
          <p className="text-lg text-[#666] max-w-3xl mx-auto leading-relaxed">
            Explore a unique blend of refreshingly tart lemonades and rich, bold coffees, all brewed to perfection. 
            Our carefully crafted beverages combine the zesty brightness of fresh lemons with the deep, aromatic notes 
            of high-end coffee beans, creating an unforgettable taste experience that awakens your senses.
          </p>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div key={index} className="bg-[#FFF8E7] rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer">
                <div className="w-full h-80 overflow-hidden">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-2xl font-bold text-[#2C5F2D] mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                    {product.name}
                  </h3>
                  <p className="text-[#666] mb-6 leading-relaxed">
                    {product.description}
                  </p>
                  <button
                    onClick={() => setOrderModal({ open: true, productName: product.name, formAction: product.formAction })}
                    className="bg-[#FFD700] text-[#2C5F2D] px-8 py-3 rounded-full font-bold hover:bg-[#FFC700] transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    ORDER NOW
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-[#FFF8E7]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div className="w-full">
              <img 
                src="https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/38bc90bf90d8ad0a7988517113164465.png"
                alt="Our Store"
                className="w-full h-auto object-contain rounded-3xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C5F2D] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                Our Story
              </h2>
              <p className="text-lg text-[#666] mb-4 leading-relaxed">
                Our story began with a simple idea: what if we combined the refreshing brightness of lemons with our sister company, <strong>Malfi Coffee&apos;s</strong> signature blend to create something truly magical. In 2025, our family turned that vision into reality, crafting a one-of-a-kind <strong>Honey Lemonade Coffee</strong> — a refreshing fusion that delights every customer.
              </p>
              <p className="text-lg text-[#666] mb-4 leading-relaxed">
                As a proud <strong>Orange County family-owned company</strong>, we are passionate about quality and craftsmanship. Every drink we serve is made with premium ingredients — from hand-selected coffee beans roasted to perfection to fresh, real lemonade.
              </p>
              <p className="text-lg text-[#666] mb-6 leading-relaxed">
                Whether you&apos;re a coffee enthusiast, a lemonade lover, or simply curious about our signature blends, we invite you to experience the perfect balance of bold and refreshing flavors — delivered straight to your home or office.
              </p>
              <div className="rounded-3xl overflow-hidden shadow-xl bg-white flex justify-center">
                <img
                  src="https://static.readdy.ai/image/0890122b367f23c3338a761be89943be/92e50dc039532929d68a9ca9fd513504.png"
                  alt="Our Story"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4">
            <div className="text-center md:text-left mb-4 md:mb-0">
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C5F2D] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                Our Customers' Reviews
              </h2>
              <p className="text-lg text-[#666] max-w-2xl">
                Hear what our amazing customers have to say about their experience
              </p>
            </div>
            <Link
              to="/submit-review"
              className="bg-[#FFD700] text-[#2C5F2D] px-8 py-3 rounded-full font-bold hover:bg-[#FFC700] transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-edit-line mr-2"></i>
              Leave a Review
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
            {allReviews.map((review, index) => (
              <div 
                key={review.id || index} 
                className="bg-[#FFF8E7] rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i 
                      key={i} 
                      className={`ri-star-fill text-lg ${i < review.rating ? 'text-[#FFD700]' : 'text-gray-300'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-[#555] text-sm leading-relaxed flex-1 mb-5">
                  "{review.review}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-[#2C5F2D]/10">
                  {review.avatar ? (
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img 
                        src={review.avatar} 
                        alt={review.name} 
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#2C5F2D] font-bold">
                        {review.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-bold text-[#2C5F2D]" style={{ fontFamily: 'Georgia, serif' }}>
                      {review.name}
                    </p>
                    <p className="text-xs text-[#999]">{review.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#2C5F2D] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Let Us Cater Your Next Event
          </h2>
          <p className="text-xl mb-8 leading-relaxed">
            Enjoy delicious lemonade and coffee drinks delivered right to your door, making it easy to stay refreshed and energized!
          </p>
          <Link 
            to="/contact#name"
            className="inline-block bg-[#FFD700] text-[#2C5F2D] px-10 py-4 rounded-full text-lg font-bold hover:bg-[#FFC700] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            GET IN TOUCH
          </Link>
        </div>
      </section>

      <Footer />

      <OrderFormModal
        isOpen={orderModal.open}
        onClose={() => setOrderModal({ open: false, productName: '', formAction: '' })}
        productName={orderModal.productName}
        formAction={orderModal.formAction}
      />
    </div>
  );
}
